<template>
  <div class="template-editor-content d-flex flex-column h-100">
    <div class="d-flex align-start justify-space-between flex-shrink-0 border-b-sm pa-6">
      <div
        v-click-outside="handleClickOutside"
        class="d-flex flex-column ga-2 flex-grow-1"
      >
        <span
          v-if="!isEditing"
          class="text-h2 text-textDarkest"
          :class="{ 'cursor-pointer': canEdit }"
          @click="startEdit"
        >
          {{ template.name }}
        </span>

        <DpplTextField
          v-else
          v-model="editName"
          label="Template Name"
          autofocus
          @keyup.enter="confirmEdit"
        />

        <p
          v-if="!isEditing"
          class="text-body-2 text-textLight"
          :class="{ 'cursor-pointer': canEdit }"
          @click="startEdit"
        >
          {{ template.description }}
        </p>

        <DpplTextArea
          v-else
          v-model="editDescription"
          label="Template Description"
          placeholder="Add a description"
          rows="2"
          @keydown.enter="confirmEdit"
        />

        <p class="text-body-2 text-textDark">
          Created <span v-if="createdBy">by {{ createdBy }}</span> on {{ createdAt }}
        </p>
      </div>

      <div class="ml-4 d-flex flex-column ga-2 align-end">
        <div class="d-flex ga-2 align-center">
          <DisableRbac :permissions="['updateApprovalTemplate']">
            <v-btn
              variant="flat"
              color="primary"
              height="32"
              rounded="lg"
              class="text-button-2 px-4"
              :disabled="saveDisabled"
              @click="$emit('save')"
            >
              Save
            </v-btn>
          </DisableRbac>

          <v-menu
            location="bottom end"
            transition="scale-transition"
          >
            <template #activator="{ props: menuProps }">
              <v-btn
                v-if="checkOneOfPermissions([
                  'createApprovalTemplate', 'updateApprovalTemplate', 'deleteApprovalTemplate',
                ])"
                v-bind="menuProps"
                variant="outlined"
                color="strokeDefault"
                height="32"
                width="40"
                rounded="lg"
                icon
              >
                <v-icon
                  icon="$dots-vertical"
                  size="20"
                  color="textDark"
                />
              </v-btn>
            </template>

            <v-list density="compact">
              <HideRbac :permissions="['createApprovalTemplate']">
                <v-list-item
                  class="text-body-2 text-textDark"
                  @click="$emit('duplicate')"
                >
                  <template #prepend>
                    <v-icon
                      icon="$content-copy"
                      size="16"
                    />
                  </template>
                  <v-list-item-title>
                    Duplicate
                  </v-list-item-title>
                </v-list-item>
              </HideRbac>

              <HideRbac :permissions="['updateApprovalTemplate']">
                <v-list-item
                  class="text-body-2 text-textDark"
                  @click="$emit('archive', !template.is_archived)"
                >
                  <template #prepend>
                    <v-icon
                      icon="mdi-archive-outline"
                      size="16"
                    />
                  </template>
                  <v-list-item-title>
                    {{ template.is_archived ? 'Unarchive' : 'Archive' }}
                  </v-list-item-title>
                </v-list-item>
              </HideRbac>

              <HideRbac :permissions="['deleteApprovalTemplate']">
                <v-list-item
                  class="text-body-2 text-error"
                  @click="openDeleteModal"
                >
                  <template #prepend>
                    <v-icon
                      icon="$delete-outline"
                      size="16"
                      color="error"
                    />
                  </template>
                  <v-list-item-title>
                    Delete
                  </v-list-item-title>
                </v-list-item>
              </HideRbac>
            </v-list>
          </v-menu>
        </div>

        <div
          v-if="!saveDisabled"
          class="d-flex align-center ga-1"
        >
          <v-icon
            icon="mdi-dot"
            size="18"
            color="primary"
          />
          <span class="text-body-3 text-primary">
            Unsaved Changes
          </span>
        </div>
      </div>
    </div>

    <div class="flex-grow-1 overflow-y-auto pa-6">
      <draggable
        class="d-flex flex-column ga-3"
        :list="template.stages"
        :disabled="!checkRbacPermissions(['updateApprovalTemplate'])"
        item-key="stage_number"
        handle=".drag-handle"
        @end="reindexStages"
      >
        <template #item="{ element: stage }">
          <TemplateStageCard
            :stage="stage"
            @remove="$emit('remove-stage', stage.stage_number)"
            @add-slot="$emit('add-slot', stage.stage_number)"
            @remove-slot="(index) => $emit('remove-slot', stage.stage_number, index)"
            @update="(name) => $emit('rename-stage', stage.stage_number, name)"
          />
        </template>
      </draggable>

      <HideRbac :permissions="['updateApprovalTemplate']">
        <v-btn
          variant="text"
          class="mt-3 text-button-2 text-textLight w-100 justify-start border-md border-dashed"
          size="large"
          @click="$emit('add-stage')"
        >
          <template #prepend>
            <v-icon
              icon="$plus"
              size="15"
            />
          </template>
          Add Stage
        </v-btn>
      </HideRbac>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import draggable from 'vuedraggable';
import { useAppAuthData } from '@/stores/app/authData';
import { useModals } from '@/stores/modals/modals';
import HideRbac from '@/components/rbac/HideRbac.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplTextArea from '@/components/datapeople/DpplTextArea.vue';
import DisableRbac from '@/components/rbac/DisableRbac.vue';
import TemplateStageCard from './TemplateStageCard.vue';

defineOptions({ name: 'TemplateEditorContent' });

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  isNew: {
    type: Boolean,
    default: false,
  },
  saveDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'save', 'duplicate', 'archive', 'rename', 'add-stage', 'remove-stage', 'add-slot', 'remove-slot', 'rename-stage',
]);

const { setModal } = useModals();
const { checkRbacPermissions, checkOneOfPermissions } = useAppAuthData();
const openDeleteModal = () => setModal({ field: 'deleteApprovalTemplate', value: true });

const canEdit = computed(() => checkRbacPermissions(['updateApprovalTemplate']));

const createdBy = computed(() => {
  if (props.isNew) return 'you';
  const cb = props.template.created_by;
  if (cb.first_name || cb.last_name) return `${cb.first_name ?? ''} ${cb.last_name ?? ''}`.trim();
  if (cb.email) return cb.email;
  return '';
});

const createdAt = computed(() => {
  const date = props.template.created_at;
  if (!date) return '';
  return String(date).slice(0, 10);
});

const isEditing = ref(false);
const editName = ref('');
const editDescription = ref('');

const startEdit = () => {
  if (!canEdit.value) return;
  editName.value = props.template.name;
  editDescription.value = props.template.description;
  isEditing.value = true;
};

const confirmEdit = () => {
  emit('rename', { name: editName.value || 'Untitled', description: editDescription.value });
  isEditing.value = false;
};

const handleClickOutside = () => {
  if (isEditing.value) confirmEdit();
};

const reindexStages = () => {
  props.template.stages.forEach((s, i) => {
    // eslint-disable-next-line no-param-reassign
    s.stage_number = i + 1;
  });
};
</script>
